import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@business/workflows/ComponentUI/Avatar';
import { routePaths } from '@utils/constants/routePaths';
import { useOutsideClickHandler } from '@utils/hooks';
import { useNotificationSocket } from '@business/services/useNotificationSocket';
import {
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationReadMutation,
  NotificationItem
} from '@ports/internal-services/notification/notificationSlice';
import {
  getUnreadCount,
  setUnreadCount,
  decrementUnread
} from '@ports/common/notificationStateSlice';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NotificationBody } from './NotificationBody';

export const NotificationBell = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unreadCount = useSelector(getUnreadCount);

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Real-time receiver (share/contribution events) + close on outside click.
  useNotificationSocket();
  useOutsideClickHandler(wrapperRef, () => setIsOpen(false));

  // Server unread count -> redux, so the socket optimistic increment and the
  // server truth share a single source for the badge.
  const { data: unreadData } = useGetUnreadNotificationCountQuery(undefined, {
    refetchOnMountOrArgChange: true
  });
  useEffect(() => {
    if (typeof unreadData?.data?.count === 'number') {
      dispatch(setUnreadCount(unreadData.data.count));
    }
  }, [unreadData, dispatch]);

  // Latest 5 for the dropdown — fetched only while open.
  const { data: listData, isFetching } = useGetNotificationsQuery(
    { page: 1, limit: 5 },
    { skip: !isOpen }
  );
  const latest = listData?.data?.rows ?? [];

  const [markRead] = useMarkNotificationReadMutation();

  const handleItemClick = async (item: NotificationItem) => {
    setIsOpen(false);
    if (!item.read) {
      dispatch(decrementUnread());
      try {
        await markRead({ id: item.id }).unwrap();
      } catch (e) {
        console.log('markRead error', e);
      }
    }
    navigate(item?.meta?.pageUrl || routePaths.Sharing);
  };

  const badge = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setIsOpen(prev => !prev)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer">
        <Bell size={24} strokeWidth={1} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-[var(--na-critical)] rounded-full">
            {badge}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-[#3F602B]">
              {t('notifications.title')}
            </span>
          </div>

          <ul className="max-h-80 overflow-y-auto">
            {isFetching && latest.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-gray-400">
                {t('loading')}…
              </li>
            )}
            {!isFetching && latest.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-gray-400">
                {t('notifications.noNotifications')}
              </li>
            )}
            {latest.map(item => (
              <li
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-[#F9FEE9] ${
                  item.read ? '' : 'bg-[#F0F7EA]'
                }`}>
                <div className="flex items-start gap-2.5">
                  <Avatar
                    src={item.meta?.profileImage}
                    alt={item.meta?.actorName || ''}
                    size="h-8 w-8"
                    className="flex-shrink-0 mt-0.5"
                  />
                  <div className="flex flex-col">
                    {item.header && (
                      <span className="text-xs font-semibold text-gray-800">
                        {item.header}
                      </span>
                    )}
                    <NotificationBody
                      content={item.content}
                      actorName={item.meta?.actorName}
                      className="text-xs text-gray-600"
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              navigate(routePaths.Notifications);
            }}
            className="w-full text-center py-2.5 text-xs font-semibold text-[var(--na-primary-color)] hover:bg-gray-50 cursor-pointer border-t border-gray-100">
            {t('viewAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
